// src/components/Footer.tsx
import { motion } from 'framer-motion';
import { HeartIcon, CpuChipIcon, ShieldCheckIcon, BoltIcon } from '@heroicons/react/24/outline';

interface FooterProps {
  onNavigate?: (page: string) => void;
}

// Social Media Icons (simplified SVG icons)
const XIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const YouTubeIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12.017 0C8.396 0 7.917.017 6.69.08 5.463.143 4.511.31 3.686.584c-.826.275-1.528.647-2.232 1.351C.75 2.639.378 3.341.103 4.167.03 5.394 0 5.873 0 9.494c0 3.621.017 4.1.08 5.327.073 1.227.24 2.179.514 3.004.275.826.647 1.528 1.351 2.232.704.704 1.406 1.076 2.232 1.351.825.275 1.777.442 3.004.514C7.917 23.983 8.396 24 12.017 24c3.621 0 4.1-.017 5.327-.08 1.227-.073 2.179-.24 3.004-.514.826-.275 1.528-.647 2.232-1.351.704-.704 1.076-1.406 1.351-2.232.275-.825.442-1.777.514-3.004.063-1.227.08-1.706.08-5.327 0-3.621-.017-4.1-.08-5.327-.073-1.227-.24-2.179-.514-3.004-.275-.826-.647-1.528-1.351-2.232C21.361.75 20.659.378 19.833.103c-.825-.275-1.777-.442-3.004-.514C16.117.017 15.638 0 12.017 0zm0 2.163c3.574 0 4.002.014 5.411.078 1.299.061 2.018.275 2.497.459.526.201.91.442 1.31.842.4.4.641.784.842 1.31.184.479.398 1.198.459 2.497.064 1.409.078 1.837.078 5.411s-.014 4.002-.078 5.411c-.061 1.299-.275 2.018-.459 2.497-.201.526-.442.91-.842 1.31-.4.4-.784.641-1.31.842-.479.184-1.198.398-2.497.459-1.409.064-1.837.078-5.411.078s-4.002-.014-5.411-.078c-1.299-.061-2.018-.275-2.497-.459-.526-.201-.91-.442-1.31-.842-.4-.4-.641-.784-.842-1.31-.184-.479-.398-1.198-.459-2.497C2.029 16.019 2.015 15.591 2.015 12.017s.014-4.002.078-5.411c.061-1.299.275-2.018.459-2.497.201-.526.442-.91.842-1.31.4-.4.784-.641 1.31-.842.479-.184 1.198-.398 2.497-.459 1.409-.064 1.837-.078 5.411-.078zm0 3.717a6.3 6.3 0 1 0 0 12.6 6.3 6.3 0 0 0 0-12.6zm0 2.163a4.137 4.137 0 1 1 0 8.274 4.137 4.137 0 0 1 0-8.274zm6.406-2.973a1.464 1.464 0 1 1-2.928 0 1.464 1.464 0 0 1 2.928 0z"/>
  </svg>
);

interface SocialIconProps {
  icon: 'x' | 'youtube' | 'instagram';
  href: string;
  label?: string;
}

const SocialIcon = ({ icon, href, label }: SocialIconProps) => {
  const icons = {
    x: <XIcon />,
    youtube: <YouTubeIcon />,
    instagram: <InstagramIcon />,
  };

  const labels = {
    x: 'X',
    youtube: 'YouTube',
    instagram: 'Instagram',
  };

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="p-2 bg-glass/50 hover:bg-glass rounded-lg transition-all hover:scale-110 text-text-muted hover:text-primary group"
      aria-label={label || labels[icon]}
    >
      <div className="transition-colors group-hover:text-primary">
        {icons[icon]}
      </div>
    </a>
  );
};

// NotesCrafter Logo component
const NotesCrafterLogo = () => (
  <img 
    src="/logo.v2.png" 
    alt="NotesCrafter Logo" 
    className="h-8 w-auto" 
    style={{ filter: 'drop-shadow(0 0 5px rgba(192, 132, 252, 0.4))' }}
  />
);

export default function Footer({ onNavigate }: FooterProps = {}) {
  const currentYear = new Date().getFullYear();

  // Helper function to handle navigation
  const handleNavigation = (page: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Scroll to top instantly so pages appear from the top
    if (typeof window !== 'undefined' && window.scrollTo) {
      try {
        window.scrollTo({ top: 0, behavior: 'instant' });
      } catch (err) {
        // Fallback for older browsers
        window.scrollTo(0, 0);
      }
    }
    
    // Navigate immediately after scroll
    if (onNavigate) {
      onNavigate(page);
    } else {
      // Fallback: dispatch custom event for navigation
      window.dispatchEvent(new CustomEvent('navigateToPage', { detail: { page } }));
    }
  };

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="mt-16"
    >
  <div className="w-full px-4 sm:px-8 lg:px-12 py-8 pl-8 md:pl-6">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <NotesCrafterLogo />
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  NotesCrafter
                </h3>
                <p className="text-xs text-text-muted">AI-Inspired PDF Enhancement</p>
              </div>
            </div>
                        <p className="text-text-muted text-sm">
              Free, privacy-first PDF tools. Process PDFs in your browser with Google Sign in. 
              Enhance, merge, and optimize documents while keeping them completely private.
            </p>
            {/* Social Media Section */}
            <div className="flex items-center space-x-3">
              <span className="text-sm text-text-muted">Follow us:</span>
              <div className="flex items-center space-x-2">
                <SocialIcon icon="youtube" href="https://youtube.com/@NotesCrafterApp" />
                <SocialIcon icon="instagram" href="https://instagram.com/notescrafterapp" />
                <SocialIcon icon="x" href="https://x.com/notescrafter" />
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text-main uppercase tracking-wide">Product</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={handleNavigation('features')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Features
                </button>
              </li>
              <li>
                <a
                  href="https://tools.notescrafter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-text-muted hover:text-primary transition-colors flex items-center gap-1.5"
                >
                  Quick Tools
                  <span className="px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-primary to-secondary text-white rounded-full leading-none">
                    NEW
                  </span>
                </a>
              </li>
              <li>
                <button onClick={handleNavigation('pricing')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Community Pass
                </button>
              </li>
              <li>
                <a
                  href="https://reviews.notescrafter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold transition-colors flex items-center gap-1.5"
                >
                  Student Reviews
                  <span className="px-1.5 py-0.2 text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded-full leading-none">
                    4.9 ★
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="https://creators.notescrafter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-text-muted hover:text-primary transition-colors"
                >
                  Creator Program
                </a>
              </li>
              <li>
                <button onClick={handleNavigation('how-to-print-online-lecture-notes')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  How to Print Online Lecture Notes
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('blog')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Blog
                </button>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text-main uppercase tracking-wide">Company</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={handleNavigation('about-us')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('community')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Community
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('support-project')} className="text-sm text-text-muted hover:text-primary transition-colors text-left flex items-center space-x-1">
                  <span>Support NotesCrafter</span>
                  <HeartIcon className="w-3 h-3 text-red-400" />
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('help-center')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Help Center
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('contact-us')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Contact Us
                </button>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-text-main uppercase tracking-wide">Legal</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={handleNavigation('privacy-policy')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Privacy Policy
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('refund-policy')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Refund Policy
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('terms-of-service')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Terms of Service
                </button>
              </li>
              <li>
                <button onClick={handleNavigation('shipping-policy')} className="text-sm text-text-muted hover:text-primary transition-colors text-left">
                  Shipping Policy
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border-glow pt-6">
          {/* Bottom Section */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* Copyright */}
            <div className="flex items-center space-x-2 text-sm text-text-muted">
              <span>© {currentYear} NotesCrafter.</span>
              <span>Made with</span>
              <HeartIcon className="w-4 h-4 text-red-400" />
              <span>for better notes.</span>
            </div>

            {/* Tech Stack */}
            <div className="flex items-center space-x-3">
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-full backdrop-blur-sm"
              >
                <CpuChipIcon className="w-3.5 h-3.5 text-green-400" />
                <span className="text-xs font-medium text-green-300">AI-Inspired</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-full backdrop-blur-sm"
              >
                <ShieldCheckIcon className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-xs font-medium text-blue-300">Secure</span>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center space-x-2 px-3 py-1.5 bg-gradient-to-r from-primary/10 to-secondary/10 border border-primary/20 rounded-full backdrop-blur-sm"
              >
                <BoltIcon className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium text-primary">Fast Processing</span>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </motion.footer>
  );
}